import { Autocomplete, Grid, TextField } from "@mui/material";
import React, { SyntheticEvent } from "react";

// This pair appeared sixteen times across the two old dialogs, each copy with
// its own error useState. Formik owns that now; a field just says what it is.

export interface FieldProps {
  id: string;
  label: string;
  xs?: number;
  form: any;
}

/** The grid geometry the dialog has always used. */
export const Cell = ({ xs = 4, children }: { xs?: number; children: React.ReactNode }) => (
  <Grid item xs={xs} sx={{ padding: "5px" }}>
    {children}
  </Grid>
);

/** A full-width row of cells. */
export const Row = ({ children }: { children: React.ReactNode }) => (
  <Grid container item xs={12} sx={{ padding: "20px" }}>
    {children}
  </Grid>
);

const errorFor = (form: any, id: string) =>
  form.touched[id] && Boolean(form.errors[id]);

const messageFor = (form: any, id: string) =>
  form.touched[id] && typeof form.errors[id] === "string" ? form.errors[id] : "";

export const TextInput = ({
  id,
  label,
  xs = 4,
  form,
  disabled,
}: FieldProps & { disabled?: boolean }) => (
  <Cell xs={xs}>
    <TextField
      id={id}
      name={id}
      placeholder={label}
      label={label}
      variant="outlined"
      fullWidth
      disabled={disabled}
      value={form.values[id] ?? ""}
      onChange={form.handleChange}
      error={errorFor(form, id)}
      helperText={messageFor(form, id)}
    />
  </Cell>
);

export interface SelectProps extends FieldProps {
  options: any[];
  /** Field name -> label. Omit for plain string options. */
  optionLabel?: (option: any) => string;
  /** Identity comparison. Omit for plain string options. */
  sameOption?: (option: any, value: any) => boolean;
  multiple?: boolean;
  onPick?: (value: any) => void;
}

export const SelectField = ({
  id,
  label,
  xs = 4,
  form,
  options,
  optionLabel,
  sameOption,
  multiple,
  onPick,
}: SelectProps) => (
  <Cell xs={xs}>
    <Autocomplete
      id={id}
      multiple={multiple}
      value={form.values[id] ?? (multiple ? [] : null)}
      options={options || []}
      getOptionLabel={optionLabel ? (o: any) => optionLabel(o) ?? "" : undefined}
      isOptionEqualToValue={sameOption}
      onChange={(_: SyntheticEvent, next: any) => {
        form.setFieldValue(id, next ?? undefined);
        onPick?.(next ?? undefined);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label}
          label={label}
          error={errorFor(form, id)}
          helperText={messageFor(form, id)}
        />
      )}
    />
  </Cell>
);

// Option adapters for the shapes the store hands back.
export const byRole = {
  optionLabel: (o: any) => o?.role_label,
  sameOption: (o: any, v: any) => o?.role_index === v?.role_index,
};

export const byClient = {
  optionLabel: (o: any) => o?.customer_name,
  sameOption: (o: any, v: any) => o?.client_index === v?.client_index,
};

export const byClientType = {
  optionLabel: (o: any) => o?.client_type_name,
  sameOption: (o: any, v: any) => o?.client_type_index === v?.client_type_index,
};

export const byClientTitle = {
  optionLabel: (o: any) => o?.client_title_name,
  sameOption: (o: any, v: any) => o?.client_title_index === v?.client_title_index,
};

export const byLabelValue = {
  optionLabel: (o: any) => o?.label,
  sameOption: (o: any, v: any) => o?.value === v?.value,
};

export const bySalesman = {
  optionLabel: (o: any) => o?.SALESMAN,
  sameOption: (o: any, v: any) => o?.SALESMAN === v?.SALESMAN,
};

export const bySchool = {
  optionLabel: (o: any) => o?.name,
  sameOption: (o: any, v: any) => o?.name === v?.name,
};
